import CloseIcon from '@mui/icons-material/Close'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import type { Appointment, AppointmentStatus } from '../../osa-scheduling/types'
import { exportDailyAppointments } from '../utils/export.utils'

interface DailyAppointmentsDialogProps {
  open: boolean
  onClose: () => void
  selectedDate: Date | null
  appointments: Appointment[]
}

const getStatusColor = (
  status: AppointmentStatus
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'new':
      return 'info'
    case 'dispatched':
      return 'warning'
    case 'cancelled':
      return 'error'
    case 'completed':
      return 'success'
    default:
      return 'default'
  }
}

const formatDateTime = (dateString: string): string => {
  try {
    return format(new Date(dateString), 'HH:mm')
  } catch (error) {
    console.error('Error formatting date:', error)
    return '-'
  }
}

const formatDate = (date: Date | null): string => {
  if (!date) return ''
  try {
    return format(date, 'EEEE, MMMM d, yyyy')
  } catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
}

const DailyAppointmentsDialog: React.FC<DailyAppointmentsDialogProps> = ({
  open,
  onClose,
  selectedDate,
  appointments,
}) => {
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  // Filter appointments for the selected date
  const dailyAppointments = appointments.filter(appointment => {
    if (!appointment.scheduleStartTime || !selectedDate) return false
    const appointmentDate = new Date(appointment.scheduleStartTime)
    return (
      appointmentDate.getFullYear() === selectedDate.getFullYear() &&
      appointmentDate.getMonth() === selectedDate.getMonth() &&
      appointmentDate.getDate() === selectedDate.getDate()
    )
  })

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setAnchorEl(null)
  }

  const handleExport = (format: 'csv' | 'xlsx') => {
    if (selectedDate) {
      exportDailyAppointments(dailyAppointments, selectedDate, format)
    }
    handleExportClose()
  }

  const columns = [
    { key: 'time', label: 'Time' },
    { key: 'name', label: 'Customer' },
    { key: 'address', label: 'Address' },
    { key: 'scout', label: 'Scout' },
    { key: 'status', label: 'Status' },
    { key: 'dataCollected', label: 'Data Collected' },
  ]

  const renderCellContent = (appointment: Appointment, columnKey: string) => {
    switch (columnKey) {
      case 'time':
        return (
          <Typography variant="body2" fontWeight="medium">
            {formatDateTime(appointment.scheduleStartTime!)}
          </Typography>
        )
      case 'name':
        return (
          <Typography variant="body2" fontWeight="medium">
            {appointment.customerName}
          </Typography>
        )
      case 'address': {
        const address = appointment.customerAddress
        if (!address || address === '-') {
          return <Typography variant="body2">-</Typography>
        }
        const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
        return (
          <MuiLink
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {address}
          </MuiLink>
        )
      }
      case 'scout': {
        const { scoutName, scoutUUID } = appointment
        if (!scoutName || scoutName === '-' || !scoutUUID) {
          return <Typography variant="body2">-</Typography>
        }
        return (
          <MuiLink
            href={`/scouts/show/${scoutUUID}`}
            onClick={e => {
              e.stopPropagation()
              navigate(`/scouts/show/${scoutUUID}`)
              e.preventDefault()
            }}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {scoutName}
          </MuiLink>
        )
      }
      case 'status':
        return (
          <Chip
            label={appointment.status}
            color={getStatusColor(appointment.status)}
            size="small"
          />
        )
      case 'dataCollected':
        return (
          <Chip
            label={appointment.dataCollected ? 'Yes' : 'No'}
            color={appointment.dataCollected ? 'success' : 'default'}
            size="small"
          />
        )
      default:
        return null
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: '90vw',
          maxWidth: '1100px',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Dispatched Projects</Typography>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {formatDate(selectedDate)} - {dailyAppointments.length} project
              {dailyAppointments.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {dailyAppointments.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportClick}
                  sx={{ mr: 1 }}
                >
                  Export
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleExportClose}>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV (.csv)</MenuItem>
                </Menu>
              </>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {dailyAppointments.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No appointments scheduled for this day</Typography>
          </Box>
        ) : (
          <Paper variant="outlined">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {columns.map(column => (
                      <TableCell key={column.key}>{column.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dailyAppointments.map(appointment => (
                    <TableRow
                      key={appointment.uuid}
                      component={Link}
                      to={`/osa-scheduling/show/${appointment.uuid}`}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      {columns.map(column => (
                        <TableCell key={column.key}>
                          {renderCellContent(appointment, column.key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default DailyAppointmentsDialog
